"use client";

import {
  useMemo,
  useState,
  useId,
  KeyboardEvent,
  useEffect,
  useRef,
} from "react";
import Link from "next/link";
import { Heading, HeadingProps } from "@repo/ui/heading";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { absoluteCmsUrl, BaseImage } from "../base-image/BaseImage";
import gsap from "gsap";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { Icon } from "@repo/ui/icon";

export type StrapiImageFmt = { url: string; width?: number; height?: number };
export type StrapiImage = {
  url: string;
  alternativeText?: string | null;
  caption?: string | null;
  name: string;
  formats?: Record<"large" | "medium" | "small" | "thumbnail", StrapiImageFmt>;
};

export type RoomItem = {
  id: number;
  title: string;
  slug: string;
  shortDescription?: string | null;
  description?: any;
  wideImage?: StrapiImage | null;
  sliderImages?: StrapiImage[] | null;
};

export type RoomsShowcaseProps = {
  heading: HeadingProps;
  rooms: RoomItem[];
};

const imgUrl = (m?: StrapiImage | null) =>
  m?.formats?.large?.url || m?.formats?.medium?.url || m?.url || "";

export default function RoomsShowcase({ heading, rooms }: RoomsShowcaseProps) {
  const list = useMemo(() => rooms.slice(0, 4), [rooms]);
  const [active, setActive] = useState(0);
  const uid = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);

  const selected = list[active];

  // Build the image set for the CURRENT room - memoized for performance
  const images = useMemo(() => {
    const g = (selected?.sliderImages ?? []).filter(Boolean) as StrapiImage[];
    const arr = g.length ? g : selected?.wideImage ? [selected.wideImage] : [];
    return arr
      .map((im, idx) => {
        const src = absoluteCmsUrl(imgUrl(im));
        return {
          key: `${selected?.id}-${idx}`,
          src,
          alt:
            im.alternativeText ||
            im.caption ||
            im.name ||
            selected?.title ||
            "Room image",
        };
      })
      .filter((x) => x.src);
  }, [
    selected?.id,
    selected?.sliderImages,
    selected?.wideImage,
    selected?.title,
  ]);

  // GSAP: description slides up each time room changes
  useEffect(() => {
    if (!containerRef.current) return;
    const desc = containerRef.current.querySelector(
      `[data-desc-key="${active}"]`,
    );
    if (desc) {
      gsap.fromTo(
        desc,
        { y: 40, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.5, ease: "power3.out" },
      );
    }
  }, [active]);

  // GSAP: image area slides up when room changes
  useEffect(() => {
    if (!imageWrapRef.current) return;
    gsap.fromTo(
      imageWrapRef.current,
      { y: 80, autoAlpha: 0, scale: 1.02 },
      { y: 0, autoAlpha: 1, scale: 1, duration: 0.6, ease: "power3.out" },
    );
  }, [selected?.id]);

  // Keyboard nav for the left list
  const onKeys = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.key === "ArrowDown" || e.key === "PageDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === "ArrowUp" || e.key === "PageUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(list.length - 1);
    }
  };

  // Keen slider state (per-room)
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    rubberband: true,
    mode: "snap",
    renderMode: "precision",
    loop: false, // set to true if you want infinite loop per room
  });

  // Reset slider to first image whenever room changes
  useEffect(() => {
    setCurrentSlide(0);
    slider.current?.moveToIdx(0);
  }, [selected?.id, slider]);

  return (
    <section
      ref={containerRef}
      className="max-w-7xl mx-auto reveal-stagger grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16 items-start py-12 w-full"
    >
      {/* LEFT: room list (tabs) */}
      <div className="flex flex-col gap-8 self-stretch justify-between md:col-span-1 col-span-full">
        <Heading
          level={heading.level}
          className="xl:text-7xl md:text-6xl text-5xl leading-tight font-noto uppercase reveal"
        >
          {heading.title}
        </Heading>

        <div className="flex flex-col gap-8">
          <ul
            role="tablist"
            aria-label="Room types"
            className="flex flex-col gap-6"
            onKeyDown={onKeys}
          >
            {list.map((room, i) => {
              const isSelected = i === active;
              const tabId = `${uid}-tab-${room.id}`;
              const panelId = `${uid}-panel-${room.id}`;

              return (
                <li
                  key={room.id}
                  role="presentation"
                  className="border-b border-gray-300 pb-6 reveal-item"
                >
                  <button
                    role="tab"
                    id={tabId}
                    aria-controls={panelId}
                    aria-selected={isSelected}
                    tabIndex={isSelected ? 0 : -1}
                    onClick={() => setActive(i)}
                    className="w-full cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
                  >
                    <p className="font-medium">{room.title}</p>
                  </button>

                  {isSelected && (
                    <div
                      data-desc-key={i}
                      role="tabpanel"
                      id={`${uid}-panel-${room.id}`}
                      aria-labelledby={`${uid}-tab-${room.id}`}
                      className="mt-3 text-gray-700 max-w-md"
                      aria-live="polite"
                    >
                      {room.shortDescription ? (
                        <p>{room.shortDescription}</p>
                      ) : Array.isArray(room.description) ? (
                        <BlocksRenderer content={room.description} />
                      ) : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="pt-2">
            <Link
              href="/rooms"
              className="relative reveal-item flex justify-center min-w-28 z-10 py-3 px-4 items-center gap-2 text-sm uppercase bg-primary text-white w-full transition"
              aria-label={`Discover more about ${selected?.title ?? "this room"}`}
            >
              Discover more
            </Link>
          </div>
        </div>
      </div>

      {/* RIGHT: slider for CURRENT room's images */}
      <div className="relative w-full reveal-item h-[340px] sm:h-[420px] md:h-[520px] lg:h-[570px] md:col-span-2 col-span-full overflow-hidden">
        <div ref={imageWrapRef} className="relative h-full">
          <div
            // key forces a fresh slider when the room changes
            key={selected?.id}
            ref={sliderRef}
            className="keen-slider h-full"
            aria-label={`Image gallery for ${selected?.title || "room"}`}
          >
            {images.map((img, i) => (
              <div
                key={img.key}
                className="keen-slider__slide relative"
                role="img"
                aria-label={`${i + 1} of ${images.length}`}
              >
                <BaseImage
                  src={img.src}
                  alt={img.alt}
                  fill
                  sizes="(max-width:1024px) 100vw, 50vw"
                  className="object-cover object-center rounded-none"
                  priority={i === 0}
                />
              </div>
            ))}
          </div>

          {/* Arrows (hidden if only one image) */}
          {images.length > 1 && (
            <>
              <button
                aria-label="Previous image"
                onClick={() => slider.current?.prev()}
                className="absolute cursor-pointer left-4 bottom-4 z-10 grid h-10 w-10 place-items-center border border-white text-white backdrop-blur hover:bg-white hover:text-primary transition"
              >
                <Icon size={20} name="arrow-left" />
              </button>
              <button
                aria-label="Next image"
                onClick={() => slider.current?.next()}
                className="absolute cursor-pointer right-4 bottom-4 z-10 grid h-10 w-10 place-items-center border border-white text-white backdrop-blur hover:bg-white hover:text-primary transition"
              >
                <Icon size={20} name="arrow-right" />
              </button>

              {/* Dots */}
              <div
                className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 space-x-2"
                role="tablist"
                aria-label="Image navigation"
              >
                {images.map((_, i) => (
                  <button
                    key={i}
                    role="tab"
                    aria-label={`Go to image ${i + 1}`}
                    aria-selected={currentSlide === i}
                    onClick={() => slider.current?.moveToIdx(i)}
                    className={`pointer-events-auto cursor-pointer inline-block h-2 w-2 transition ${
                      currentSlide === i ? "bg-white" : "bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
